/**
 * Jarak Haversine — PERKIRAAN untuk memandu layar, bukan angka yang disimpan.
 *
 * Jarak dan `inside_geofence` yang tercatat SELALU dihitung server (`GeoDistancePort`,
 * ADR-0036 D4); klien tidak pernah mengirimkannya. Salinan di sini ada untuk satu alasan:
 * kolom alasan "di luar radius" harus muncul SEBELUM tombol ditekan. Tanpa perkiraan lokal,
 * pengguna baru mengetahui kewajiban itu setelah permintaan ditolak — di lapangan, dengan
 * jaringan lambat, sambil memegang barang.
 *
 * Rumusnya sengaja dijaga identik dengan `haversine-distance.calculator.ts` di API, termasuk
 * jari-jari Bumi, agar perkiraan layar dan keputusan server tidak berselisih di tepi radius.
 */

/** Jari-jari Bumi rata-rata dalam meter (WGS-84 mean radius) — sama dengan server. */
const EARTH_RADIUS_METERS = 6_371_000;

export interface GeoPoint {
  readonly latitude: number;
  readonly longitude: number;
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function distanceMeters(a: GeoPoint, b: GeoPoint): number {
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);
  const deltaLat = toRadians(b.latitude - a.latitude);
  const deltaLon = toRadians(b.longitude - a.longitude);

  const sinLat = Math.sin(deltaLat / 2);
  const sinLon = Math.sin(deltaLon / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));

  return EARTH_RADIUS_METERS * c;
}

export interface WorkLocationLike {
  readonly id: string;
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly geofenceRadiusMeters: number;
  readonly isActive: boolean;
}

export interface NearestResult {
  readonly id: string;
  readonly name: string;
  readonly distanceMeters: number;
  readonly geofenceRadiusMeters: number;
}

/**
 * Lokasi kerja AKTIF terdekat. Lokasi nonaktif diabaikan — sama seperti server, yang menyaring
 * `isActive` sebelum menghitung. Mengikutsertakannya akan membuat layar menjanjikan lokasi yang
 * tidak akan pernah dipilih server.
 */
export function nearestWorkLocation(
  point: GeoPoint,
  locations: readonly WorkLocationLike[],
): NearestResult | null {
  let best: NearestResult | null = null;
  for (const location of locations) {
    if (!location.isActive) continue;
    const distance = distanceMeters(point, location);
    if (!best || distance < best.distanceMeters) {
      best = {
        id: location.id,
        name: location.name,
        distanceMeters: distance,
        geofenceRadiusMeters: location.geofenceRadiusMeters,
      };
    }
  }
  return best;
}
